import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Colors } from './types';

import { cn } from './lib/utils';

type BadgeVariant =
  | 'default'
  | 'contained'
  | 'outlined'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ksl';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer',
  {
    variants: {
      variant: {
        contained:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        outlined: 'text-foreground',
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        ksl: 'border-transparent bg-red-800 hover:scale-110 transform transition text-white',
      },
      color: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        success: 'bg-success text-success-foreground hover:bg-success-hover',
        error: 'bg-error text-error-foreground hover:bg-error-hover',
        info: 'bg-info text-info-foreground hover:bg-info-hover',
        warning: 'bg-warning text-warning-foreground hover:bg-warning-hover',
        gray: 'bg-gray-300 text-gray-700 font-semibold hover:bg-gray-600-hover',
        lightGray: 'bg-gray-100 text-foreground hover:bg-gray-600-hover',
      },
    },
    compoundVariants: [
      ...(
        [
          'primary',
          'secondary',
          'success',
          'error',
          'info',
          'warning',
        ] as Colors[]
      ).flatMap((color) => [
        {
          variant: 'outlined' as BadgeVariant,
          color,
          className: `border-${color} text-${color} bg-transparent hover:bg-gray-500/5 hover:border-${color}-hover`,
        },
      ]),
    ],
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, color, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, color }), className)}
      {...props}
    />
  );
}

function BadgeAdornment({ children, ...props }: BadgeProps) {
  return (
    <Badge
      variant={'destructive'}
      className="absolute -top-5 -right-5"
      {...props}
    >
      {children}
    </Badge>
  );
}

export { Badge, BadgeAdornment, badgeVariants };
